import { useMutation } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { PartSimilarityMatch } from "~/features/parts/components/part-similarity-dialog";

// AIDEV-NOTE: React Query mutation hook for triggering part similarity suggestions

interface SuggestPartSimilaritiesVariables {
    sessionId: string;
}

interface SuggestPartSimilaritiesResponse {
    suggestions: PartSimilarityMatch[];
}

/**
 * Hook to trigger part similarity suggestions for a session.
 * Calculates similarities in-memory without storing them.
 */
export function useSuggestPartSimilarities() {
    return useMutation<
        SuggestPartSimilaritiesResponse,
        Error,
        SuggestPartSimilaritiesVariables
    >({
        mutationFn: async ({ sessionId }) => {
            const response = await fetch(`/api/parts/suggest-matches`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({ sessionId }),
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    throw new Error("Failed to suggest part similarities");
                }
                throw new Error(
                    errorData.error || "Failed to suggest part similarities",
                );
            }

            const data = await response.json();
            return data;
        },
        onError: (error) => {
            console.error("Failed to suggest part similarities:", error);
            // Only show toast on client to avoid SSR issues
            if (typeof window !== "undefined") {
                toast({
                    title: "Error",
                    description:
                        error.message || "Failed to suggest part similarities",
                    variant: "destructive",
                });
            }
        },
    });
}
